import { HostClientType } from '@microsoft/teams-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MAKE_A_CALL_KEYS } from '../../../adapter-interfaces/src';
import { Logger, downloadMobileLogs, getLocalStorageItem, getMSTeamsContext } from '../../../logger/src';
import {
  WebexMobileSearchContacts,
  WebexMobileSearchContactsHandle
} from '../WebexMobileSearchContacts/WebexMobileSearchContacts';
import { CallButtons } from '../components/CallButtons';
import { NumberPad } from '../components/NumberPad';
import { useMakeCall } from '../hooks/useMakeCall';
import useWebexClasses from '../hooks/useWebexClasses';

import { ButtonCircle, ButtonPill, Flex, IconNext } from '@momentum-ui/react-collaboration';
import { useTranslation } from 'react-i18next';
import './WebexMobileDialpad.scss';
// import { CLIENT_TYPE_IOS, CLIENT_TYPE_IPADOS, navigateToWebexUrl } from '../utils/commonUtils';
import { pages } from '@microsoft/teams-js';
import { IWebexIntContact } from '@webex/component-adapter-interfaces/dist/esm/src';
import { submitMetrics } from '../../src/services/WebexMetricsService';
import { IChannelData } from '../utils/CallBackUrl';
import { INTERNAL_OPERATIONS, MobileDialpad, MobileOpenWebexClickedMetric, audioCall, crossLaunchCallMetric, videoCall } from '../utils/MetricUtils';
import { buildMobileCallingUrl } from '../utils/mobileCallingUrl';
import { navigateToWebexUrl } from '../utils/commonUtils';

type Props = {
  style?: React.CSSProperties;
  src: string;
  user?: IWebexIntContact[];
  channelData?: IChannelData;
  clientType: string;
  myNumber?: string;
  showOpenWebex?: boolean;
  showKeypad?: boolean;
  trailingAction?: React.ReactNode;
  keypadPortalTarget?: Element | null;
  searchLabel?: string;
  onSearchInputChange?: (input: string) => void;
};

export type WebexMobileDialpadHandle = {
  focusSearch: () => void;
  clearSearch: () => void;
};

export type IWebexMobileDialpadHandle = WebexMobileDialpadHandle;

/**
 * Full dialpad with searchable input, number pad, and calling buttons
 *
 * @param root0
 * @param root0.style
 * @returns {JSX.Element} React component
 */
export const WebexMobileDialpad = React.forwardRef<
  WebexMobileDialpadHandle,
  Props
>(function WebexMobileDialpad(
  {
    style = undefined,
    src,
    clientType,
    myNumber = undefined,
    showOpenWebex = true,
    showKeypad = true,
    trailingAction = undefined,
    keypadPortalTarget = undefined,
    searchLabel = undefined,
    onSearchInputChange = undefined,
  }: Props,
  ref
) {
  const [cssClasses, sc] = useWebexClasses('dialpad-widget');
  const displayMyNumber = myNumber?.trim();
  const searchContactsRef = useRef<WebexMobileSearchContactsHandle>(null);
  const [callAddress, setCallAddress] = useState<string>('');
  const { t } = useTranslation();
  const [callDisabled, setCallDisabled] = useState<boolean>(false);
  const [makeACallToken, setMakeACallToken] = useState<string>('');
  const [makeCall] = useMakeCall();
  React.useImperativeHandle(ref, () => ({
    focusSearch() {
      searchContactsRef.current?.focusInput();
    },
    clearSearch() {
      searchContactsRef.current?.clearInput();
      searchContactsRef.current?.blurInput();
      setCallAddress('');
    },
  }));

  const onButtonPress = (value: string) => {
    // When dialpad buttons are pressed, pass 'dialpad' as the input source
    // This prevents the search dropdown from appearing, matching Webex App behavior
    searchContactsRef.current?.appendValueToInput(value, 'dialpad');
  };

  const backSpaceAction = () => {
    // Explicitly declare dialpad intent before performing backspace
    // This keeps all dialpad-origin actions consistent (digits + backspace)
    searchContactsRef.current?.appendValueToInput('', 'dialpad');
    searchContactsRef.current?.backSpaceAction();
  }; 
  
  const callButtonPressAction = () => {
    searchContactsRef.current?.callButtonPressAction();
  };

  const onInputChange = useCallback(
    (input: string) => {
      setCallDisabled(false);
      setCallAddress(input);
      onSearchInputChange?.(input);
    },
    [onSearchInputChange, setCallDisabled, setCallAddress]
  );

  const onClickOpenWebex = () => {
    const url = navigateToWebexUrl;
    const webexUrl = new URL(url);
    window.open(webexUrl.href);
    submitMetrics(
      {
        metricEvent: MobileOpenWebexClickedMetric
      }
    );
  }
  const fetchClientType = async () => {
    const context = await getMSTeamsContext();
    if(context?.page?.subPageId) {
      const botObject = context.page.subPageId.split(" ");
      if(botObject[0] === "BotService") {
          Logger.info('Deep linking happened via Bot Service');

          if(clientType == HostClientType.android) {
            Logger.info('Handling Bot Service deep link on Android');
            searchContactsRef.current?.clearInput();
            // Deep link input is treated as keyboard input (can trigger search)
            searchContactsRef.current?.appendValueToInput(botObject[1], 'keyboard');
            return;
          }
          makeCrossLaunchCall(botObject[1], botObject[2].toLowerCase() === 'true');
      }
      else {
        Logger.info('Deep link did not originate from Bot Service');
      }
    }
    else {
      Logger.info('Deep linking failed: missing subPageId');
    }
  }

  useEffect(() => {
    fetchClientType();
  }, []);
  
  const makeCrossLaunchCall = (address: string, video?: boolean) => {
    if(address) {
      if(address.toLowerCase() === INTERNAL_OPERATIONS.SAVE.toLowerCase()) {
        downloadMobileLogs();
      }
      else {
        address = address.replace(/ /g, "");
        const url = getCallingUrl(address, video);
        const callUrl = new URL(url);
        // To Be Removed Later once cross launch callback support available for android
        // if (clientType == CLIENT_TYPE_IOS || clientType == CLIENT_TYPE_IPADOS) {
        if(src === 'chatExtension') {
          pages.backStack.navigateBack();
        }
        // }
        Logger.info(
          `Making ${video ? 'video' : 'audio'} call from the mobile ${src} surface`
        );
        window.open(callUrl.href);
        submitMetrics(
          {
            metricEvent: crossLaunchCallMetric,
            callMadeSection: `${MobileDialpad}`,
            callType: `${video? videoCall : audioCall}`
          }
        );
        callButtonPressAction();
    }
  }
};
  const getCallingUrl =  (address: string, video?: boolean) => {
    return buildMobileCallingUrl(address, video);
}
  useEffect(() => {
    const handleLocalStorageChange = (event: StorageEvent) => {
      const stringValue = getLocalStorageItem<string>(MAKE_A_CALL_KEYS.MAKE_A_CALL_TOKEN);
      if (stringValue) {
        setMakeACallToken(stringValue);
      } else {
        setMakeACallToken('');
      }
    };
    window.addEventListener('storage', handleLocalStorageChange);

    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
    };
  }, []);

  const keypadPanel = showKeypad ? (
    <div data-testid="mobile-keypad-panel" className={sc('keypad-panel')}>
      <NumberPad onButtonPress={onButtonPress} />
      <Flex justifyContent="flex-end" xgap="1.2rem">
      <CallButtons
        address={callAddress}
        className={sc('call-buttons')}
        disabled={callDisabled}
        useMakeAudioCall={() => makeCrossLaunchCall(callAddress, false)}
        useMakeVideoCall={() => makeCrossLaunchCall(callAddress, true)}
      />
      <div className={sc('backspace-delete')}>
      <ButtonCircle className={sc('backspace-delete-button')} onPress={backSpaceAction} size={28} ghost key="1" aria-label={t('deleteButton')}>
          <IconNext scale={18} name="backspace" weight="bold" />
      </ButtonCircle>
      </div>
      </Flex>
      {displayMyNumber && (
        <div
          className={sc('my-number')}
          role="group"
          aria-label={`${t('label.topbar.number')} ${displayMyNumber}`}
        >
          <span>{t('label.topbar.number')}</span>
          <strong>{displayMyNumber}</strong>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={cssClasses} style={style}>
      <div className={sc('search-row')}>
        <WebexMobileSearchContacts
          ref={searchContactsRef}
          label={searchLabel}
          onInputChange={onInputChange}
          onDropdownHide={() => setCallDisabled(false)}
          onUserSelect={(user) => setCallDisabled(!!user)}
          makeCall = {makeCrossLaunchCall}
          enableFeatureParitySearch={src === 'main'}
        />
        {trailingAction}
      </div>

      {keypadPanel && keypadPortalTarget
        ? createPortal(keypadPanel, keypadPortalTarget)
        : keypadPanel}
      {showOpenWebex && (
        <>
          <div className={sc('open-webex-button')}>
            <ButtonPill
              ghost
              outline
              size={40}
              className={sc('open-webex')}
              onPress={onClickOpenWebex}
            >
              <span className={sc('open-webex-text')}>{t('openWebex')}</span>
            </ButtonPill>
          </div>
          <span className={sc('info-text')}>{t('infoText')}</span>
        </>
      )}
      <div>
      </div>
    </div>
  );
});
